import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import _ from 'lodash';
import { IDataChart } from '@ts/ui-models';
import { useTheme } from '@mui/material/styles';
import { Stack, Box, Typography } from '@mui/material';
import { generateColorShades } from '@utils/chart/chart.utils';

const stackedBarChartOptions: ApexOptions = {
  chart: {
    type: 'bar',
    height: 450,
    stacked: true,
    toolbar: {
      show: false,
    },
    animations: {
      enabled: true,
      //easing: 'easeinout',
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 1500,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 700,
      },
    },
  },
  plotOptions: {
    bar: {
      horizontal: true,
      dataLabels: {
        total: {
          enabled: true,
          offsetX: 0,
          style: {
            fontSize: '13px',
            fontWeight: 900,
          },
        },
      },
    },
  },
  stroke: {
    width: 1,
    colors: ['#fff'],
  },
  xaxis: {
    labels: {
      formatter: val => {
        return val + 'u.';
      },
    },
  },
  yaxis: {
    title: {
      text: undefined,
    },
  },
  tooltip: {
    y: {
      formatter: val => {
        return val + 'u.';
      },
    },
  },
  fill: {
    opacity: 1,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'left',
    offsetX: 40,
  },
};

const mockQuantityData = (): IDataChart => {
  return {
    xaxis: {
      categories: ['Warehouse', 'Factory', 'Yard', 'In transit', 'Suppliers', 'Clients', 'Maintenance', 'Lost/Unknown'],
    },
    series: [
      {
        name: 'Pallets-Wood',

        data: [44, 55, 41, 37, 22, 43, 2, 0],
      },
      {
        name: 'Pallet-Plastic',

        data: [53, 32, 33, 52, 13, 43, 6, 0],
      },
      {
        name: 'Small container',

        data: [12, 17, 11, 9, 15, 11, 2, 7],
      },
      {
        name: 'Foldable RTI',

        data: [19, 17, 25, 28, 16, 29, 20, 0],
      },
      {
        name: 'Cages',

        data: [25, 12, 19, 32, 25, 24, 1, 0],
      },
    ],
  };
};

const mockQuantityData1 = (): IDataChart => {
  return {
    xaxis: {
      categories: [
        'Road, Accesss',
        'Harbour area',
        'Build. A',
        'Build. B-3 B-6',
        'Build. D',
        'Build. M',
        'Canteen and locker rooms',
        'Others',
      ],
    },
    series: [
      {
        name: 'Team1',

        data: [44, 55, 41, 37, 22, 43, 2, 0],
      },
      {
        name: 'Team2',

        data: [53, 32, 33, 52, 13, 43, 6, 0],
      },
      {
        name: 'Team3',

        data: [12, 17, 11, 9, 15, 11, 2, 7],
      },
      {
        name: 'Team4',

        data: [19, 17, 25, 28, 16, 29, 20, 0],
      },
      {
        name: 'Team5',

        data: [25, 12, 19, 32, 25, 24, 1, 0],
      },
    ],
  };
};

const mockQuantityData2 = (): IDataChart => {
  return {
    xaxis: {
      categories: [
        'T1-Arrivals',
        'T1-Main hall',
        'T1-Departures',
        'T3-North',
        'T3-South',
        'T3-Parking',
        'Parking F1',
        'Parking F2',
      ],
    },
    series: [
      {
        name: 'Trolley-Luggage',
        data: [44, 55, 41, 37, 22, 43, 2, 0],
      },
      {
        name: 'Trolley-Compact',
        data: [53, 32, 33, 52, 13, 43, 6, 0],
      },
      {
        name: 'Cart shopper',
        data: [12, 17, 11, 9, 15, 11, 2, 7],
      },
      {
        name: 'Vehicles',
        data: [19, 17, 25, 28, 16, 29, 20, 0],
      },
      {
        name: 'Whellchair',
        data: [25, 12, 19, 32, 25, 24, 1, 0],
      },
      {
        name: 'Others',
        data: [25, 12, 19, 32, 25, 24, 1, 0],
      },
    ],
  };
};

interface ChartProps {
  type?: number;
}

const BarChart = (props: ChartProps) => {
  //#region Styles
  const theme = useTheme();
  let color = ['#025fa6', '#00b877', '#e6a015', '#e3324c', '#6e51cf'];
  color = props.type === 1 ? color : ['#6e51cf', '#1F3B4D', '#90EE90', '#2E8B57', '#FFA500'];
  const { primary, secondary } = theme.palette.text;
  const info = theme.palette.primary.main;
  //#endregion

  const [series] = useState(
    props.type === 1 ? mockQuantityData().series : props.type === 2 ? mockQuantityData2().series : mockQuantityData1().series
  );

  const [options, setOptions] = useState<ApexOptions>(stackedBarChartOptions);

  useEffect(() => {
    setOptions(prevState => ({
      ...prevState,
      ...(props.type === 1 && { colors: color }),
      xaxis: {
        categories:
          props.type === 1
            ? mockQuantityData().xaxis?.categories
            : props.type === 2
            ? mockQuantityData2().xaxis?.categories
            : mockQuantityData1().xaxis?.categories,
        labels: {
          style: {
            colors: [info],
          },
        },
      },
      tooltip: {
        theme: 'light',
      },
      ...(props.type === 2 && {
        theme: {
          mode: 'light',
          palette: 'palette10',
          monochrome: {
            enabled: false,
            color: '#255aee',
            shadeTo: 'light',
            shadeIntensity: 0.65,
          },
        },
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, info, secondary]);

  return (
    <div style={{ overflow: 'auto', position: 'relative' }} className={''}>
      <div style={{ minWidth: '100%', minHeight: '502px' }}>
        <ReactApexChart
          options={options}
          series={series}
          // @ts-ignore
          type={stackedBarChartOptions.chart?.type}
          height={stackedBarChartOptions.chart?.height}
        />
      </div>
    </div>
  );
};

export default BarChart;

/* <Box sx={{ p: 3, pb: 0 }}>
        <Stack spacing={2}>
          <Typography variant="h6" textAlign={'end'}>
            Total: {_.sum(mockQuantityData().series[0].data)}
          </Typography>
        </Stack>
      </Box> */
